import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fileShareHttp } from './service/fileshareHttp';

const FileShareContext = createContext(null);

// Yalnızca ilk yükleme sırasındaki görünüm için kullanılan varsayılan değerler.
// Paylaşımlar ve işlem kayıtları ASLA tarayıcıya kalıcı olarak yazılmaz.
const DEFAULT_SETTINGS = {
  defaultDays: 7,
  maxDays: 30,
  maxFileSizeMb: 2048,
  maxShareSizeMb: 5120,
  maxActiveShares: 50,
  allowPasswords: true,
};

export function FileShareProvider({ children }) {
  const [shares, setShares] = useState([]);
  const [adminShares, setAdminShares] = useState([]);
  const [events, setEvents] = useState([]);
  const [adminEvents, setAdminEvents] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isAdmin = ['admin', 'it_support'].includes(localStorage.getItem('user_role'));

  const refresh = useCallback(async () => {
    // Public sayfa portal oturumu gerektirmez ve JWT istekleri üretmez.
    if (window.location.pathname.startsWith('/share/')) return;
    setLoading(true);
    setError('');
    try {
      const [ownShares, cfg, ownEvents] = await Promise.all([
        fileShareHttp.list(), fileShareHttp.settings(), fileShareHttp.events(),
      ]);
      setShares(ownShares);
      setSettings(cfg);
      setEvents(ownEvents);
      if (isAdmin) {
        const [allShares, allEvents] = await Promise.all([
          fileShareHttp.adminShares(), fileShareHttp.adminEvents(),
        ]);
        setAdminShares(allShares);
        setAdminEvents(allEvents);
      }
    } catch (e) {
      setError(e?.message || 'Paylaşım kayıtları yüklenemedi.');
      throw e;
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    refresh().catch(() => {}); // Ekrana error state olarak gösterilir.
  }, [refresh]);

  const createShare = async (params) => {
    const share = await fileShareHttp.create(params);
    setShares((prev) => [share, ...prev.filter((s) => s.id !== share.id)]);
    if (isAdmin) setAdminShares((prev) => [share, ...prev.filter((s) => s.id !== share.id)]);
    // Başarılı yüklemenin sonucunu kullanıcıya döndür; yenilemeyi paralel yap.
    refresh().catch(() => {});
    return share;
  };

  const upsert = (share) => {
    setShares((prev) => prev.map((s) => s.id === share.id ? share : s));
    setAdminShares((prev) => prev.map((s) => s.id === share.id ? share : s));
  };

  const revokeShare = async (id) => {
    const share = await fileShareHttp.revoke(id);
    upsert(share);
    refresh().catch(() => {});
    return share;
  };

  const extendShare = async (id, days) => {
    const share = await fileShareHttp.extend(id, days);
    upsert(share);
    refresh().catch(() => {});
    return share;
  };

  const updateSettings = async (patch) => {
    const cfg = await fileShareHttp.updateSettings({ ...settings, ...patch });
    setSettings(cfg);
    refresh().catch(() => {});
    return cfg;
  };

  return <FileShareContext.Provider value={{
    shares, adminShares, events, adminEvents, settings, loading, error, isAdmin,
    refresh, createShare, revokeShare, extendShare, updateSettings,
  }}>
    {children}
  </FileShareContext.Provider>;
}

export function useFileShare() {
  const context = useContext(FileShareContext);
  if (!context) throw new Error('FileShareProvider eksik.');
  return context;
}
